import type { JobModel, ViewJobDetailsModel, ViewJobThirdPartyApply } from "./ApplyButtonTypes";
import type { ButtonSize, ButtonVariant } from "../../../../../../mads/Button";

import {
  resolveRawClickSurface,
  triggerJobClickTracking,
  triggerRawClickLogging,
} from "./jobClickTracking";
import { usePassportLoginGate } from "./PassportLoginModal";
import { type BoxRef, Button, NewWindow } from "../../../../../../mads";
import { useTheme } from "../../../../../../mads/ThemeProvider";
import { useLogErrorOnce } from "../../../../../../shared/logging/useLogErrorOnce";
import { useTrackVisibility } from "../../../../../../shared/logging/visibility-tracker";
import { setCookiesForViewJob } from "../../../../../../shared/native-modules/core/setCookies";
import {
  type JstBroadcastSubscription,
  subscribe,
} from "../../../../../../shared/native-modules/tare/jstBroadcast";
import { JSTModalScreen } from "../../../../../../shared/native-modules/tare/JSTModalScreen";
import { openModal } from "../../../../../../shared/native-modules/tare/openModal";
import {
  canShowPushPrompt,
  requestReengagementModal,
  requestSystemPushAuthWithPrimer,
} from "../../../../../../shared/native-modules/tare/pushAuthorization";
import { useTareData } from "../../../../../../shared/native-modules/tare/TareDataContext";
import { CookieKey } from "../../../../../../shared/native-modules/types/RNCoreNativeModule";
import eventEmitter, { ApplyType, ApplyStatus } from "../../../../../../shared/util/eventEmitter";
import { measureElementInWindow } from "../../../../../../shared/util/measureElementInWindow";
import { httpGetJson } from "../../../../../native-module/httpGetJson";
import { navigateView } from "../../../../../native-module/navigateView";
import { updateApplyStartCookie } from "../../../../../native-module/updateApplyStartCookie";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import {
  MapExternalApplyMigrateTst,
  MegAccelPostThirdpartyApplyPushPrimerTst,
} from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { getEnvironmentConfig } from "../../../../config/urls";
import { useDwellTiming } from "../../../../context/DwellTiming";
import {
  useDomainUrl,
  useJobClickTrackingAttributes,
  useViewJobDataContext,
  useViewjobOneGraphModel,
  useViewjobSourceUrl,
} from "../../../../context/ViewJobDataContext";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";
import { useIsDesktop } from "../../../../context/ViewJobScreenDisplay";
import { useEmployerOutreachInfo } from "../../../../hooks/useEmployerOutreachInfo";
import { useEmployerOutreachSource } from "../../../../hooks/useEmployerOutreachSource";
import { useGFJAutoClickApply } from "../../../../hooks/useGFJAutoClickApply";
import { getAutoClickUrl } from "../../../../utils/autoClickApplyUtils";
import { markPostApplyAdPending } from "../../PostApplyAd/postApplyAdPending";
import { handleEmployerOutreachApply } from "../EmployerOutreach/utils/handleEmployerOutreachApply";
import { StickyShadow } from "../StickyShadow/StickyShadow";
import { type ButtonDimensions, logApplyButtonShowEvent } from "../utils/applyButtonLogging";

import { spacing } from "@indeed/ifl-tokens/react-native";
import { RNApplyButtonTypeV3 } from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/RNApplyButtonTypeV3";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AccessibilityInfo, Platform, StyleSheet, useWindowDimensions } from "react-native";

async function showPushPromptAfterApply(): Promise<void> {
  try {
    const result = await canShowPushPrompt();
    if (result?.canShowPushPrompt) {
      if (result.authStatus !== "denied") {
        await requestSystemPushAuthWithPrimer("bottomSheet", "rnviewjob");
      } else {
        await requestReengagementModal("bottomSheet", "rnviewjob");
      }
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Sets the desktop click-tracking cookie (RCLK) for the apply-start request
 */
async function setRclkClickCookie(
  clickCookieName: string | null | undefined,
  clickCookieValue: string | null | undefined,
): Promise<void> {
  if (clickCookieName !== CookieKey.desktopClickTrackingLog || !clickCookieValue) {
    return;
  }
  await setCookiesForViewJob([
    { name: CookieKey.desktopClickTrackingLog, value: clickCookieValue },
  ]);
}

export type ApplyButtonProps = {
  testID?: string;
  newTabLabel: string;
  isLoading: boolean;
  hasCtaEnticer?: boolean;
  shouldHaveShadow?: boolean;
  viewJobButtonLinkModel: ViewJobThirdPartyApply;
  jobModel: JobModel;
  viewJobDetailsModel: ViewJobDetailsModel;
  hasLoggedSeen: boolean;
  setHasLoggedSeen: (value: boolean) => void;
  buttonSize?: ButtonSize | undefined;
  buttonVariant?: ButtonVariant | undefined;
  onApplyPress?: () => void;
};

export function createApplyLink(linkModelHref: string | undefined, domain: string) {
  if (!linkModelHref) {
    return undefined;
  }
  try {
    // linkModelHref is a full URL
    return new URL(linkModelHref).toString();
  } catch {
    // linkModelHref is a relative URL
    return new URL(linkModelHref, domain).toString();
  }
}

export function ApplyButton({
  testID,
  newTabLabel,
  isLoading,
  viewJobButtonLinkModel,
  jobModel,
  viewJobDetailsModel,
  shouldHaveShadow,
  hasLoggedSeen,
  setHasLoggedSeen,
  buttonSize = "lg",
  buttonVariant = "primary",
  onApplyPress,
}: ApplyButtonProps) {
  const theme = useTheme();
  const domain = useDomainUrl();
  const sourceUrl = useViewjobSourceUrl();
  const { key: jobKey, title: jobTitle } = jobModel;
  const { appShareUrl } = viewJobButtonLinkModel;
  const { jobCountry: country, mobvjtk: mobtk } = viewJobDetailsModel;
  const applyLink = createApplyLink(viewJobButtonLinkModel.href, domain);
  const [hasLoggedVisibility, setHasLoggedVisibility] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const buttonRef = useRef<BoxRef>(null);
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const ogModel = useViewjobOneGraphModel();
  const { accountKey, ctk, employerOutreachEngagement } = ogModel ?? {};
  const invite = employerOutreachEngagement?.engagements?.[0];
  const tareData = useTareData();
  const buildEnvironment = tareData?.appModel?.buildEnvironment;
  const environmentConfig = getEnvironmentConfig(buildEnvironment);
  const rezcowBaseUrl = environmentConfig.rezcowBaseUrl;
  const resumeBaseUrl = environmentConfig.resumeBaseUrl;

  const { isFromEmployerOutreach, outreachSource } = useEmployerOutreachSource();
  const { outreachInfo } = useEmployerOutreachInfo(jobKey ?? "") ?? {};
  const { initialData, from } = useViewJobDataContext();
  const rnViewJobDisplay = useRnViewJobDisplay();
  const isLoggedIn = tareData?.accountStatus?.isLoggedIn ?? false;
  // Desktop 2-pane: the viewjob renders inside the embedded SERP pane, which
  // gates via a Passport redirect rather than the in-page modal (see usePassportLoginGate).
  const isDesktopEmbedded = rnViewJobDisplay === "DESKTOP_EMBEDDED";
  // Desktop web (embedded JSF page or standalone RN Web) uses a tighter
  // vertical rhythm than the mobile app; shrink the button height to match.
  // The desktop display modes only occur on web, so `useIsDesktop()` already
  // implies web here.
  const isDesktop = useIsDesktop();

  const passportUrl = ogModel?.thirdPartyApplyCreateAccountModel?.contextualSignInPassportURL ?? "";
  const autoClickUrl = getAutoClickUrl({
    isLoggedIn,
    passportUrl,
    applyLink,
    isWeb: Platform.OS === "web",
  });
  // The third-party GFJ auto-click is wired below, after the apply-start logging
  // callbacks are defined, so it can log the apply-start before navigating.

  // For an auto-opened (first-render) job in a desktop two-pane feed, the user never
  // clicked the job card, so the job click must be counted when they click apply.
  // Present only on web for the auto-open case; native and manual-click jobs leave it
  // undefined. Fire at most once per mount.
  const jobClickTrackingAttributes = useJobClickTrackingAttributes();
  const hasFiredJobClickTracking = useRef(false);

  const isWebViewOpen = useRef(false);
  // Snapshot of isLoginGateActive read inside performApply. The gate value is computed
  // by usePassportLoginGate further down, so we cannot capture it in the performApply
  // closure directly without creating a circular dependency.
  const isLoginGateActiveRef = useRef(false);
  const thirdPartyApplyPushPrimerProctor = useViewjobProctorAllocation(
    MegAccelPostThirdpartyApplyPushPrimerTst,
  );
  const externalApplyMigrateProctor = useViewjobProctorAllocation(MapExternalApplyMigrateTst);
  const isExternalApplyMigrateActive =
    externalApplyMigrateProctor.value === MapExternalApplyMigrateTst.buckets.Test;
  // ApplyButton call-to-action text
  const buttonContent = viewJobButtonLinkModel.content;

  const willAppearCallbackRef = useRef<() => void>(undefined);
  willAppearCallbackRef.current = () => {
    if (isWebViewOpen.current) {
      isWebViewOpen.current = false;

      if (jobKey) {
        // External applies open a third-party website in a webview, so we cannot
        // determine whether the user actually completed the application.
        eventEmitter.emit("finishApply", {
          jobKeys: [jobKey],
          applyType: ApplyType.EXTERNAL_APPLY,
          status: ApplyStatus.UNKNOWN,
        });
      }

      // Focus the button after returning from the webview
      if (buttonRef.current) {
        AccessibilityInfo.sendAccessibilityEvent(buttonRef.current, "focus");
      }

      const isThirdPartyApplyPushPrimerProctorActive =
        thirdPartyApplyPushPrimerProctor.value ===
        MegAccelPostThirdpartyApplyPushPrimerTst.buckets.Test;
      if (isThirdPartyApplyPushPrimerProctorActive) {
        void showPushPromptAfterApply();
      }
    }
  };

  useEffect(() => {
    let subscription: JstBroadcastSubscription | null = null;
    let isMounted = true;

    const stableCallback = () => {
      willAppearCallbackRef.current?.();
    };

    const setup = async () => {
      const sub = await subscribe("ViewWillAppear", stableCallback);
      if (isMounted) {
        subscription = sub;
      } else {
        // remove subscription if component is unmounted. This prevents a potential memory
        // leak if the component is unmounted before the subscribe promise resolves.
        void sub?.remove();
      }
    };
    void setup();

    return () => {
      isMounted = false;
      void subscription?.remove();
    };
  }, []);

  const { markApplyStart } = useDwellTiming();

  // Mirror of legacy basecamp behavior (jobsearch-ui ViewJobButton.tsx): when the apply
  // click is gated by the Passport login modal, fire the /applystart endpoint with
  // log=1 as an image pixel so the apply intent is recorded server-side at gate time
  // (the server treats log=1 as log-only and skips the third-party redirect).
  const logThirdPartyApplyIntercept = useCallback(
    (isAutoClick = false) => {
      if (Platform.OS !== "web" || !applyLink) {
        return;
      }
      const interceptUrl = `${applyLink}${applyLink.includes("?") ? "&" : "?"}log=1`;
      if (isAutoClick) {
        // The auto-click navigates the current tab immediately, which cancels an
        // in-flight Image request; a keepalive fetch survives the navigation so the
        // gated apply intent is still logged (matches the manual click + jobseeker-frontend).
        // Swallow rejections (network error, keepalive body cap) — logging is best-effort.
        void fetch(interceptUrl, { keepalive: true, mode: "no-cors" }).catch(() => {});
      } else {
        const trackingImage = new globalThis.Image();
        trackingImage.src = interceptUrl;
      }
    },
    [applyLink],
  );

  const logThirdPartyApplyStart = useCallback(
    (isAutoClick = false) => {
      if (!mobtk) {
        return;
      }
      const baseUrl = Platform.OS === "web" ? window.location.origin : domain;
      if (!baseUrl) {
        return;
      }
      const thirdPartyApplyLogUrl = new URL("/m/rpc/log/apply-start", baseUrl);
      thirdPartyApplyLogUrl.searchParams.set("showPassportModel", isLoggedIn ? "0" : "1");
      thirdPartyApplyLogUrl.searchParams.set("tk", mobtk);
      thirdPartyApplyLogUrl.searchParams.set("surface", rnViewJobDisplay);
      thirdPartyApplyLogUrl.searchParams.set("spn", initialData?.sponsored ? "1" : "0");
      // isTrusted: a manual tap is a real (trusted) click; the GFJ auto-click is a
      // synthetic/programmatic navigation, so it reports iststd=0 (matches jobseeker-frontend).
      thirdPartyApplyLogUrl.searchParams.set("iststd", isAutoClick ? "0" : "1");
      if (Platform.OS === "web") {
        if (isAutoClick) {
          // The auto-click navigates the current tab immediately; fire with keepalive so
          // the request survives the navigation, and synchronously rather than gating on
          // the async RCLK cookie write (which may not finish first — the server-provided
          // click value wins anyway).
          // Swallow rejections (network error, keepalive body cap) — logging is best-effort.
          void fetch(thirdPartyApplyLogUrl.toString(), { keepalive: true }).catch(() => {});
        } else {
          // Set the RCLK click-tracking cookie before firing the request so it is attached
          // to the same-origin apply-start fetch. The server-provided value always wins.
          void setRclkClickCookie(
            viewJobButtonLinkModel.clickCookieName,
            viewJobButtonLinkModel.clickCookieValue,
          ).finally(() => {
            // Swallow rejections (e.g. network error) — logging is best-effort,
            // matching the auto-click and native paths.
            void fetch(thirdPartyApplyLogUrl.toString()).catch(() => {});
          });
        }
      } else {
        // On native, route through the native bridge so authenticated cookies/headers
        // are attached. The server marks the job as MAYBE_APPLIED based on this call;
        // the response body is irrelevant so we swallow any error (e.g. non-JSON body).
        void httpGetJson(thirdPartyApplyLogUrl.toString()).catch(() => {});
      }
    },
    [
      mobtk,
      isLoggedIn,
      rnViewJobDisplay,
      initialData?.sponsored,
      domain,
      viewJobButtonLinkModel.clickCookieName,
      viewJobButtonLinkModel.clickCookieValue,
    ],
  );

  // Third-party apply button fires only on the non-IA flag (mirrors jobseeker-frontend).
  // Before navigating, log the apply-start (+ intercept when the login gate is active) so the
  // GFJ auto-click is recorded in the third-party apply-start funnel / MAYBE_APPLIED exactly
  // like a manual tap. iststd=0 marks it as a synthetic (non-user-initiated) click.
  useGFJAutoClickApply(autoClickUrl, mobtk, jobKey, ogModel?.autoClickApplyNonIaFlag, () => {
    logThirdPartyApplyStart(true);
    if (isLoginGateActiveRef.current) {
      logThirdPartyApplyIntercept(true);
    }
  });

  const logThirdPartyButtonLoad = useCallback(() => {
    if (Platform.OS !== "web" || !mobtk) {
      return;
    }
    const thirdPartyButtonLoadLogUrl = new URL(
      "/m/rpc/log/3p-apply-button-load",
      window.location.origin,
    );
    thirdPartyButtonLoadLogUrl.searchParams.set("tk", mobtk);
    void fetch(thirdPartyButtonLoadLogUrl.toString());
  }, [mobtk]);

  const performApply = useCallback(async () => {
    // Set loading state immediately to prevent double-tap during async operations
    setIsNavigating(true);

    // Timeout fallback: if navigation doesn't complete within 3 seconds, allow retry
    const timeoutId = setTimeout(() => {
      setIsNavigating(false);
    }, 3000);

    try {
      // Auto-open (desktop two-pane) parity: when the viewjob was auto-opened the
      // user never clicked the job card, so the job click is counted here on apply.
      // Mirrors legacy web ClickTime/ViewJobButtonLinkContainer. Web-only and fires
      // at most once; manual-click jobs leave jobClickTrackingAttributes undefined.
      // The matching job-card rawclick is emitted below with the same one-time gate.
      const shouldFireAutoOpenJobClick =
        Platform.OS === "web" && !!jobClickTrackingAttributes && !hasFiredJobClickTracking.current;
      if (shouldFireAutoOpenJobClick) {
        hasFiredJobClickTracking.current = true;
        triggerJobClickTracking(jobClickTrackingAttributes);
      }

      // Apply-start rawclick (web only): fires on every apply click, independent of
      // the auto-open job-card-click beacons above. isFirstAutoLoad reflects whether
      // this was the auto-opened first job (jobClickTrackingAttributes presence is the
      // signal).
      if (Platform.OS === "web") {
        const rawClickAttrs = {
          tk: mobtk,
          jobKey,
          sponsored: !!initialData?.sponsored,
        };
        const autoOpenJobClickRawClickAttrs = {
          ...rawClickAttrs,
          tk: jobClickTrackingAttributes?.tk ?? mobtk,
        };
        const rawClickOptions = {
          surface: resolveRawClickSurface(from, rnViewJobDisplay),
          ia: false,
          isFirstAutoLoad: !!jobClickTrackingAttributes,
        };

        if (shouldFireAutoOpenJobClick) {
          // first auto load job card click
          triggerRawClickLogging(autoOpenJobClickRawClickAttrs, isLoggedIn, {
            ...rawClickOptions,
            isApplyStartClick: false,
          });
        }

        // apply start click
        triggerRawClickLogging(rawClickAttrs, isLoggedIn, {
          ...rawClickOptions,
          isApplyStartClick: true,
        });
      }
      // Mark apply start for dwell timing
      markApplyStart();
      onApplyPress?.();
      // TODO WENG-633 implement logging
      logThirdPartyApplyStart();
      // When the Passport login gate will intercept this click, also fire the
      // /applystart endpoint with log=1 so the apply intent is recorded at gate
      // time rather than only after the user completes sign-in.
      if (isLoginGateActiveRef.current) {
        logThirdPartyApplyIntercept();
      }
      //set apply start cookie
      if (jobKey) {
        void updateApplyStartCookie(jobKey, "v", domain);
      }

      // Web only: record a pending 3P-apply intent so PostApplyAd's
      // tab-return listener can trigger the modal when the user comes
      // back from the new tab. The Passport-gated click goes through
      // SuccessfulSignInModal.web.tsx, which marks pending on its own
      // path — skip the mark here when the gate intercepts.
      if (Platform.OS === "web" && !isLoginGateActiveRef.current && jobKey && mobtk) {
        markPostApplyAdPending({ jobKey, mobvjtk: mobtk });
      }

      // Log employer outreach apply if this is an employer outreach job
      if (isFromEmployerOutreach && jobKey && invite && rezcowBaseUrl && resumeBaseUrl) {
        await handleEmployerOutreachApply({
          invite,
          rezcowBaseUrl,
          resumeBaseUrl,
          jobKey,
          outreachSource,
          outreachType: outreachInfo?.outreachType,
          accountKey,
          ctk,
          mobtk,
        });
      }
      if (Platform.OS !== "web" && applyLink) {
        if (isExternalApplyMigrateActive) {
          isWebViewOpen.current = true;
          void openModal(JSTModalScreen.externalApply(applyLink), "rnviewjob")
            .then(() => {
              willAppearCallbackRef.current?.();
              if (jobKey && mobtk) {
                eventEmitter.emit("externalApplyReturn", { jobKey, mobvjtk: mobtk });
              }
            })
            .catch(() => {
              isWebViewOpen.current = false;
            });
        } else {
          // Note: the promise here never resolves on iOS, and only resolved on error
          // on android, so await will prevent the rest of the method from running
          void navigateView(sourceUrl, applyLink, appShareUrl ? appShareUrl : "", jobTitle);
          isWebViewOpen.current = true;
        }
      }
    } finally {
      clearTimeout(timeoutId);
      setIsNavigating(false);
    }
  }, [
    sourceUrl,
    applyLink,
    jobKey,
    domain,
    jobTitle,
    appShareUrl,
    markApplyStart,
    isFromEmployerOutreach,
    invite,
    rezcowBaseUrl,
    resumeBaseUrl,
    outreachSource,
    outreachInfo,
    accountKey,
    ctk,
    mobtk,
    logThirdPartyApplyStart,
    logThirdPartyApplyIntercept,
    onApplyPress,
    isExternalApplyMigrateActive,
    jobClickTrackingAttributes,
    isLoggedIn,
    initialData?.sponsored,
    rnViewJobDisplay,
    from,
  ]);

  const { isLoginGateActive, loginGateHref, onPressApply, modal } = usePassportLoginGate({
    applyLink,
    isLoggedIn,
    contextualSignInPassportURL: passportUrl,
    onApply: performApply,
    mobtk,
    isDesktopEmbedded,
    jobKey,
  });
  isLoginGateActiveRef.current = isLoginGateActive;

  useEffect(() => {
    logThirdPartyButtonLoad();
  }, [logThirdPartyButtonLoad]);

  useLayoutEffect(() => {
    if (!hasLoggedVisibility && jobKey && buttonRef.current) {
      measureElementInWindow(buttonRef.current, ({ x, y, width, height }) => {
        const windowDimensions: ButtonDimensions = {
          srcWidth: screenWidth,
          srcHeight: screenHeight,
          iaButtonX: x,
          iaButtonY: y,
          iaButtonWidth: width,
          iaButtonHeight: height,
        };
        logApplyButtonShowEvent(jobKey, RNApplyButtonTypeV3.thirdParty, mobtk, windowDimensions);
        setHasLoggedVisibility(true);
      });
    }
  }, [hasLoggedVisibility, jobKey, mobtk, screenHeight, screenWidth]);

  useTrackVisibility(buttonRef, {
    trackingConfig: {
      provider: "ViewJob",
      trackingKey: mobtk ?? "",
      componentName: "ThirdPartyApplyButton",
    },
    onBecomeVisible: () => {
      if (!hasLoggedSeen) {
        setHasLoggedSeen(true);
      }
    },
    enabled: !hasLoggedSeen,
  });

  // The gate opens the in-page modal only in the standalone case (gate active
  // with no redirect href); there the button has no href, so the new-tab
  // affordance is suppressed. Otherwise the button is an anchor that opens a new
  // tab: the third-party apply link (gate inactive) or, in desktop 2-pane, the
  // Passport redirect (loginGateHref).
  const opensModalGate = isLoginGateActive && loginGateHref == null;
  const buttonHref = isLoginGateActive ? loginGateHref : applyLink;

  return (
    <>
      <StickyShadow enabled={shouldHaveShadow}>
        <Button
          ref={buttonRef}
          testID={testID}
          onPress={() => {
            void onPressApply();
          }}
          aria-label={opensModalGate ? buttonContent : `${buttonContent} (${newTabLabel})`}
          iconAfter={
            !opensModalGate &&
            country !== "JP" && (
              <NewWindow
                aria-hidden
                size="xs"
                fill={buttonVariant === "secondary" ? theme.colors.foregroundActionPrimary : "#fff"}
              />
            )
          }
          size={buttonSize}
          loading={isLoading || isNavigating}
          href={buttonHref}
          hrefAttrs={{ rel: "nofollow", target: "_blank" }}
          variant={buttonVariant}
          sx={isDesktop ? applyButtonStyles.desktopWeb : undefined}
        >
          {!isLoading && buttonContent}
        </Button>
      </StickyShadow>
      {modal}
    </>
  );
}

type OGWrapperOmitKeys = "viewJobDetailsModel" | "jobModel" | "viewJobButtonLinkModel";
export type OGWrappedApplyButtonProps = Omit<ApplyButtonProps, OGWrapperOmitKeys>;

export function OGWrappedApplyButton({
  testID,
  newTabLabel,
  isLoading,
  shouldHaveShadow,
  hasLoggedSeen,
  setHasLoggedSeen,
  buttonSize,
  buttonVariant = "primary",
  onApplyPress,
}: OGWrappedApplyButtonProps) {
  const ogModel = useViewjobOneGraphModel();
  const { thirdPartyApply, job } = ogModel ?? {};
  const logErrorOnce = useLogErrorOnce();

  if (ogModel == null || thirdPartyApply == null || job == null) {
    if (!job?.isExpired) {
      logErrorOnce("Apply button missing: ThirdPartyApply button not rendered", {
        jobKey: job?.jobKey,
        hasOgModel: ogModel != null,
        hasThirdPartyApply: thirdPartyApply != null,
        hasJob: job != null,
      });
    }
    return null;
  }

  const jobModel: JobModel = {
    key: job.jobKey,
    title: job.details?.title,
  };
  const viewJobDetailsModel: ViewJobDetailsModel = {
    mobvjtk: ogModel.mobvjtk,
    jobCountry: ogModel.job.jobCountry,
  };

  return (
    <ApplyButton
      testID={testID}
      newTabLabel={newTabLabel}
      isLoading={isLoading}
      viewJobButtonLinkModel={thirdPartyApply}
      jobModel={jobModel}
      viewJobDetailsModel={viewJobDetailsModel}
      shouldHaveShadow={shouldHaveShadow}
      hasLoggedSeen={hasLoggedSeen}
      setHasLoggedSeen={setHasLoggedSeen}
      buttonSize={buttonSize}
      buttonVariant={buttonVariant}
      onApplyPress={onApplyPress}
    />
  );
}

const applyButtonStyles = StyleSheet.create({
  // Desktop web uses a tighter vertical rhythm than the mobile app; shrink the
  // apply button height to match legacy web viewjob.
  desktopWeb: {
    paddingTop: spacing.sc3,
    paddingBottom: spacing.sc3,
  },
});
